import { useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import { formatDateTime, formatHour } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { ui } from "../lib/ui";
import type { AutoFetchEvent } from "../types";
import { Panel } from "./Panel";
import { Switch } from "./Switch";

const REFRESH_MS = 5000;

const OUTCOME: Record<AutoFetchEvent["outcome"], string> = {
  queued: "Downloaded, run queued",
  empty: "No recordings",
  failed: "Failed",
  skipped: "Skipped",
};

/** The scheduled fetch: every few hours, the days of the last month that
 *  were never processed come down from the NVR and are queued. */
export function AutoFetchPanel({ onActivity }: { onActivity?: () => void }) {
  const info = useAsync(() => api.autoFetch(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") info.reload();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [info.reload]);

  // The page's downloads and runs refresh when a check starts or ends.
  const running = info.data?.running ?? false;
  const downloadId = info.data?.current_download_id ?? null;
  useEffect(() => {
    onActivity?.();
  }, [running, downloadId, onActivity]);

  const act = async (call: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      info.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const data = info.data;
  const hours = data?.hours.map(formatHour).join(", ");

  return (
    <Panel
      title="Automatic fetch"
      subtitle={
        data
          ? `At ${hours}: every day of the previous ${data.lookback_days} not processed yet is downloaded from the NVR and its run queued — demographics ${data.faces ? "on" : "off"}, debug video ${data.annotate ? "on" : "off"}.`
          : undefined
      }
      about={`A day is fetched when nothing is queued or running for it and its last run neither succeeded nor was cancelled — only runs decide, never the counts on file, since a recording that starts before midnight spills its last crossings into the next day. Newest first, one download at a time; it waits while ${data?.max_pending ?? 2} runs are already queued so the disk holds only a couple of days ahead of the workers. A day the NVR has no recordings for is asked again after a day. Days left alone for another reason are listed below with it.`}
      actions={
        data && (
          <div className="flex items-center gap-3">
            <Switch
              label="Scheduled"
              checked={data.enabled}
              onChange={(enabled) => act(() => api.setAutoFetch(enabled))}
            />
            <button
              type="button"
              className={`${ui.btnQuiet} whitespace-nowrap`}
              disabled={busy || !data.available || data.running}
              onClick={() => act(() => api.checkAutoFetchNow())}
            >
              Check now
            </button>
          </div>
        )
      }
    >
      {error && <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>}
      {info.error && <p className={`${ui.notice} ${ui.noticeError}`}>{info.error}</p>}
      {data && !data.available && (
        <p className={ui.notice}>
          The schedule runs inside the desktop app; this console is served by a bare API, so
          nothing is fetched on its own here.
        </p>
      )}
      {data && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
          <dt className="text-muted">Now</dt>
          <dd className="m-0">
            {data.running ? (data.activity ?? "Checking…") : "Idle"}
          </dd>
          <dt className="text-muted">Next check</dt>
          <dd className="m-0">
            {data.available && data.enabled ? formatDateTime(data.next_check_at) : "—"}
          </dd>
          <dt className="text-muted">Last check</dt>
          <dd className="m-0">
            {data.last_started_at
              ? `${formatDateTime(data.last_started_at)}${data.last_summary ? ` — ${data.last_summary}` : ""}`
              : "Not since the app started"}
          </dd>
        </dl>
      )}
      {data && data.excluded.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <p className="mb-1.5 text-[13px] text-muted">
            Set aside by the last check — no run has succeeded for these days, and it will
            not fetch them on its own:
          </p>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Day</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {data.excluded.map((item) => (
                <tr key={`${item.branch_id}-${item.day}`}>
                  <td className="whitespace-nowrap">{item.day}</td>
                  <td className="text-muted">{item.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.recent.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className={ui.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Day</th>
                <th>Outcome</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((event) => (
                <tr key={`${event.at}-${event.branch_id}-${event.day}`}>
                  <td className="whitespace-nowrap">{formatDateTime(event.at)}</td>
                  <td className="whitespace-nowrap">{event.day}</td>
                  <td
                    className={`whitespace-nowrap ${event.outcome === "failed" ? "text-danger" : event.outcome === "queued" ? "text-success" : "text-muted"}`}
                  >
                    {OUTCOME[event.outcome]}
                    {event.run_id !== null && ` (#${event.run_id})`}
                  </td>
                  <td className="text-muted">{event.message ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
